import React, { useLayoutEffect, useMemo, useRef } from 'react';
import { useVirtualizer } from '@tanstack/react-virtual';
import {
  estimateOpenColumnCityGroupSize,
  OPEN_COLUMN_CITY_GAP_PX,
  OPEN_COLUMN_CITY_VIRTUAL_ENABLED,
  OPEN_COLUMN_CITY_VIRTUAL_MIN,
} from '../utils/openColumnCityVirtual';
import { remeasureMountedVirtualItems } from '../utils/remeasureVirtualItems';
import { useVirtualizerScrollElement } from '../hooks/useVirtualizerScrollElement';
import './OpenColumnVirtualCityList.css';

/**
 * Lista virtual de cidades no scroll de OpenColumnBoard (Em aberto).
 * scrollElementRef = ref do .open-column-body (pai).
 */
export function OpenColumnVirtualCityList({
  groups = [],
  scrollElementRef,
  enabled = OPEN_COLUMN_CITY_VIRTUAL_ENABLED,
  overscan = 4,
  children,
}) {
  const list = Array.isArray(groups) ? groups : [];
  const useVirtual = enabled && list.length >= OPEN_COLUMN_CITY_VIRTUAL_MIN;
  const fallbackParentRef = useRef(null);
  const virtualHostRef = useRef(null);
  const { scrollEl, viewportVersion } = useVirtualizerScrollElement(
    scrollElementRef,
    fallbackParentRef,
    [useVirtual, list.length]
  );

  const rowVirtualizer = useVirtualizer({
    count: useVirtual ? list.length : 0,
    getScrollElement: () => scrollEl,
    estimateSize: (index) => estimateOpenColumnCityGroupSize(list[index]),
    // Espaçamento entre cidades fica no virtualizer (nunca margin do card):
    // margin fora do box medido é o que gerava gaps de tamanhos diferentes.
    gap: OPEN_COLUMN_CITY_GAP_PX,
    overscan,
    measureElement:
      typeof window !== 'undefined' && navigator.userAgent.indexOf('Firefox') === -1
        ? (el) => {
            if (!el) return 0;
            return el.offsetHeight || el.getBoundingClientRect().height || 0;
          }
        : undefined,
  });

  // Assinatura de altura: remedir quando a cidade ganha/perde OS, não só na troca de count.
  const sizeSignature = useMemo(
    () =>
      list
        .map((g) => `${g?.cidade ?? ''}:${Array.isArray(g?.ordens) ? g.ordens.length : 0}`)
        .join('|'),
    [list]
  );

  useLayoutEffect(() => {
    if (!useVirtual || !scrollEl) return;
    remeasureMountedVirtualItems(virtualHostRef.current, rowVirtualizer);
  }, [useVirtual, scrollEl, viewportVersion, sizeSignature, rowVirtualizer]);

  if (!useVirtual) {
    return (
      <div className="open-column-city-list open-column-city-list--plain" ref={fallbackParentRef}>
        {list.map((group, index) => (
          <React.Fragment key={group?.cidade ?? `city-${index}`}>
            {typeof children === 'function' ? children(group, index) : null}
          </React.Fragment>
        ))}
      </div>
    );
  }

  const virtualItems = rowVirtualizer.getVirtualItems();
  // Nunca mostrar spacer vazio com dados: até o virtualizer medir o viewport, plain.
  const usePlainFallback = list.length > 0 && virtualItems.length === 0;

  if (usePlainFallback) {
    return (
      <div className="open-column-city-list open-column-city-list--plain" data-virtual-fallback="1">
        {list.map((group, index) => (
          <React.Fragment key={group?.cidade ?? `city-${index}`}>
            {typeof children === 'function' ? children(group, index) : null}
          </React.Fragment>
        ))}
      </div>
    );
  }

  return (
    <div
      className="open-column-city-list open-column-city-list--virtual"
      ref={virtualHostRef}
      style={{
        height: `${rowVirtualizer.getTotalSize()}px`,
        width: '100%',
        position: 'relative',
      }}
    >
      {virtualItems.map((virtualRow) => {
        const group = list[virtualRow.index];
        const key = group?.cidade ?? virtualRow.key;
        return (
          <div
            key={key}
            className="open-column-virtual-item"
            data-index={virtualRow.index}
            ref={rowVirtualizer.measureElement}
            style={{
              position: 'absolute',
              top: 0,
              left: 0,
              width: '100%',
              transform: `translateY(${virtualRow.start}px)`,
            }}
          >
            {typeof children === 'function' ? children(group, virtualRow.index) : null}
          </div>
        );
      })}
    </div>
  );
}

export default OpenColumnVirtualCityList;
